import { TrackingManagerV1 } from '@volkswagen-onehub/tracking-service';
import { GiveawayResponse } from '@org-avp/avp-hck-dga-customerui-commons';
import { Brand } from '@org-avp/models';
import { MyDealerConfigurationFeatureServiceV2 } from '@volkswagen-onehub/mydealer-configuration-service/lib/v2';
import { computeDealerType } from '@volkswagen-onehub/tracking-service/lib/v1/compute-dealer-type';
import { extractBrand } from '../brand';
import {
  Configuration,
  EventInfoBrand,
  PartialEventData,
  Partner,
  TrackingEvent,
} from './definitions';
import { extractConfiguration } from './model-configuration';
import { extractMyDealerId } from '../dealer-data';

export class TrackingService {
  private featureAppId?: string;

  private featureAppVersion? = process.env.FEATURE_APP_VERSION ?? undefined;

  private brand?: EventInfoBrand;

  private brochureId?: string;

  private configuration?: Configuration;

  private country?: string;

  private language?: string;

  private partner?: Partner;

  private isUserAuthenticated?: boolean;

  constructor(
    private trackingManager?: TrackingManagerV1,
    private myDealerConfigService?: MyDealerConfigurationFeatureServiceV2,
  ) {}

  updateEventData(giveawayData?: GiveawayResponse): void {
    if (!giveawayData) {
      return;
    }
    this.language = giveawayData.locale.substring(0, 2);
    this.country = giveawayData.locale.substring(3, 5);
    this.featureAppId = `DigitalGiveaway-D6-${this.country}`;
    const brand = giveawayData.brand ?? extractBrand(giveawayData.marketingCode);
    switch (brand) {
      case Brand.VW:
        this.brand = EventInfoBrand.passenger;
        break;
      case Brand.VW_NUTZFAHRZEUGE:
        this.brand = EventInfoBrand.commercial;
        break;
      default:
    }

    this.brochureId = giveawayData.id;
    this.configuration = extractConfiguration(giveawayData);

    this.setFeatureAppTrackingData();
    this.updatePartnerData(giveawayData);
  }

  private updatePartnerData(giveawayData: GiveawayResponse) {
    const dealerId = extractMyDealerId(giveawayData.dealerId);

    if (this.myDealerConfigService && dealerId) {
      void this.myDealerConfigService.getDealer(dealerId).then((data) => {
        if (!data) {
          return;
        }

        this.partner = {
          informationBnr: dealerId,
          informationName: data.legalName || data.displayName,
          informationType: computeDealerType(data, this.brand),
          informationBrand: this.brand,
          informationCountry: giveawayData.market,
        };
      });
    }
  }

  setUserIsAuthenticated(isAuthenticated: boolean): void {
    this.isUserAuthenticated = isAuthenticated;
  }

  setFeatureAppTrackingData(): void {
    if (this.trackingManager) {
      this.trackingManager.contextualEventData = {
        event: {
          environment: {
            featureAppId: this.featureAppId,
            language: this.language,
            country: this.country,
          },
        },
      };
    }
  }

  track(event: TrackingEvent, additionalData?: PartialEventData): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const partialEvent: { [key: string]: any } = {
      eventInfo: { brand: this.brand, ...additionalData?.eventInfo },
      environment: {
        featureAppId: this.featureAppId,
        featureAppIdAppVersion: this.featureAppVersion,
      },
      user: { loginStatus: this.isUserAuthenticated },
      content: { BrochureId: this.brochureId, ...additionalData?.content },
      ...(additionalData?.link && { link: additionalData?.link }),
      ...(additionalData?.video && { video: additionalData?.video }),
      ...(additionalData?.social && { social: additionalData?.social }),
      ...(additionalData?.accordion && { accordion: additionalData?.accordion }),
      ...(additionalData?.process && { process: additionalData?.process }),
      ...(this.partner && { partner: this.partner }),
      ...(this.configuration && { configuration: this.configuration }),
    };

    this.trackingManager?.track(event, partialEvent);
  }
}
